/**
 * Single module for all lead and form submissions.
 * - Sheets: one Google Apps Script Web App URL (hardcoded). Payload must include formType, origin, route.
 *   Apps Script expects JSON body; adds "Timestamp (Server)" server-side.
 * - ERP: sync to ERP API (hardcoded base URL); service types sent as-is from the form.
 * No env variables; no caller-overridable URLs.
 */

const LEADS_SHEETS_URL =
  "https://script.google.com/macros/s/AKfycbwOTMSuujLp_ZGpEdJ-QQSEOvvbFEFlWH18xO12zplBmYSAjq4_s2eDyppSfainH0ZBlg/exec";

const ERP_API_BASE_URL = "https://api.reportengine-prod.ezeetask.com/api/v1/erp/";
const ERP_LEAD_SYNC_ENDPOINT = `${ERP_API_BASE_URL}lead/sync`;

/**
 * Current website origin for filtering by site (e.g. https://housemaster.com).
 * Returns "" on server.
 */
export function getSiteOrigin() {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin;
  }
  return "";
}

/**
 * POST payload to the single leads sheet. Payload must include formType, origin, route.
 * Adds site from getSiteOrigin() when missing. Used by checkout, vendor, etc.
 * @param {Object} payload - Must include formType, origin, route
 * @returns {Promise<{ sheetsSuccess: boolean, message: string }>}
 */
export async function submitToSheets(payload) {
  const result = { sheetsSuccess: false, message: "" };
  if (!LEADS_SHEETS_URL) {
    result.message = "Leads sheet URL not configured";
    return result;
  }
  try {
    const site = payload.site != null && payload.site !== "" ? payload.site : getSiteOrigin();
    const payloadWithSite = { ...payload, site };
    await fetch(LEADS_SHEETS_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payloadWithSite),
    });
    result.sheetsSuccess = true;
    result.message = "Saved to sheet successfully";
    return result;
  } catch (err) {
    result.message = `Google Sheets Error: ${err.message}`;
    return result;
  }
}

/**
 * Submit lead to ERP (if configured) and always to the single leads sheet.
 * Callers must pass formType, origin, route in additionalData.
 * @param {Object} leadData
 * @param {string} leadData.name - Required
 * @param {string} leadData.phone - Required
 * @param {string} [leadData.cityId]
 * @param {string} [leadData.otherCity]
 * @param {string} [leadData.city] - Back-compat (treated as cityId if cityId missing)
 * @param {string} leadData.serviceType - Required
 * @param {string} [leadData.email]
 * @param {string} [leadData.otherServiceType]
 * @param {Object} [leadData.additionalData] - Must include formType, origin, route
 * @returns {Promise<{ erpSuccess: boolean, sheetsSuccess: boolean, message: string }>}
 */
export async function submitLeadToERP(leadData) {
  const {
    name,
    email,
    phone,
    cityId,
    otherCity,
    city,
    serviceType,
    otherServiceType = "",
    additionalData = {},
  } = leadData;

  const result = { erpSuccess: false, sheetsSuccess: false, message: "" };

  const normalizedOtherCity =
    otherCity && String(otherCity).trim() !== "" ? String(otherCity).trim() : "";
  const normalizedCityId =
    cityId && String(cityId).trim() !== ""
      ? String(cityId).trim()
      : city && String(city).trim() !== ""
        ? String(city).trim()
        : "";

  if (!name || !phone || (!normalizedCityId && !normalizedOtherCity) || !serviceType) {
    result.message = "Missing required fields for lead submission";
    return result;
  }

  if (ERP_LEAD_SYNC_ENDPOINT) {
    try {
      const apiPayload = {
        email: email && String(email).trim() !== "" ? String(email).trim() : "",
        leadName: name,
        mobileNo: phone,
        customServiceType: serviceType,
        customOtherServiceType: String(otherServiceType || ""),
        requestType: "Product Enquiry",
        source: "Website",
      };

      if (normalizedOtherCity) apiPayload.otherCity = normalizedOtherCity;
      else apiPayload.cityId = normalizedCityId;

      const apiResponse = await fetch(ERP_LEAD_SYNC_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(apiPayload),
      });
      if (apiResponse.status === 200) {
        result.erpSuccess = true;
        result.message = "Lead submitted to ERP successfully";
      } else {
        result.message = `ERP API returned status ${apiResponse.status}`;
      }
    } catch (apiError) {
      result.message = `ERP API Error: ${apiError.message}`;
    }
  } else {
    result.message = "ERP not configured, using Google Sheets only";
  }

  if (LEADS_SHEETS_URL) {
    try {
      const site = additionalData.site != null && additionalData.site !== "" ? additionalData.site : getSiteOrigin();
      const sheetsData = {
        formType: additionalData.formType ?? "",
        origin: additionalData.origin ?? "",
        route: additionalData.route ?? "",
        site,
        name,
        fullName: name,
        email: email || "",
        phone,
        mobileNumber: phone,
        location: normalizedOtherCity || additionalData.cityName || normalizedCityId,
        city: normalizedOtherCity || additionalData.cityName || normalizedCityId,
        serviceType,
        service: serviceType,
        ...additionalData,
      };
      await fetch(LEADS_SHEETS_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sheetsData),
      });
      result.sheetsSuccess = true;
      if (result.message) result.message += " | Google Sheets backup successful";
      else result.message = "Lead saved to Google Sheets successfully";
    } catch (sheetsError) {
      result.message = `Google Sheets Error: ${sheetsError.message}`;
    }
  } else {
    if (!result.message) result.message = "Warning: No Google Sheets URL configured";
  }

  if (!result.erpSuccess && !result.sheetsSuccess) {
    result.message = "Failed to submit lead to both ERP and Google Sheets";
  }

  return result;
}
